<template>
  <div class="financial-selector">
    <StepHeader
      v-observe-visibility="{
        callback: animationVisibilityChanged,
        intersection: {
          threshold: 0.60
        },
      }"
      title="Select Your Bank"
      :subtitle="isFoundation? 'Verify your income to check your eligibility and set your payment schedule.' : 'Verify your income and receive funds in your preferred bank account.'"
    />

    <div
      class="financial-selector__search"
      :class="{
        'financial-selector__search--sticky': stickySearchBar,
        'hidden': invisibleStickySearchBar && stickySearchBar}"
    >
      <AppSearchInput
        class="md:mx-auto md:px-25 max-w-550"
        :placeholder="placeholder"
        :search-text="searchText"
        :show-back-button="searching"
        @input="handleInput"
        @focus="handleFocus"
        @close="handleClose"
      />

      <div
        v-if="stickySearchBar"
        class="financial-selector__search-list"
      >
        <div
          v-for="(bank, index) in filteredInstitutions"
          :key="index"
          class="py-20 cursor-pointer"
          @click="handleSelect(bank)"
        >
          {{ bank.name }}
        </div>
      </div>
    </div>

    <div
      v-if="!searchText"
      class="flex justify-center"
    >
      <p
        v-if="isFoundation"
        class="text-xs mt-10"
      >
        Ensure you’re eligible by verifying your banking information.
      </p>
      <p
        v-else
        class="text-xs mt-10"
      >
        Secure the best rate by verifying your bank.
      </p>
      <img
        class="mx-5 mt-5 cursor-pointer"
        :src="require('@/assets/svg/plaid/question.svg')"
        @click="showPopup = true"
      >
    </div>

    <FinancialSelectorBanks
      :banks="banks"
      :searching="searching"
      :sticky-search-bar="stickySearchBar"
      :filtered-institutions="filteredInstitutions"
      :handle-bank-select="handleBankSelect"
      :handle-select="handleSelect"
      :search-text="searchText"
    />

    <FinancialSelectorFooter />

    <div
      v-observe-visibility="{
        callback: handleInvisibility,
        intersection: {
          threshold: 1.0
        },
      }"
    />

    <FinancialSelectorPopup
      v-if="showPopup"
      @close="showPopup = false"
    />
  </div>
</template>

<script>
import AppSearchInput from '@/components/common/inputs/AppSearchInput'
import StepHeader from '@/components/common/step-layout/StepHeader'
import navigation from '@/mixins/navigation'
import FinancialSelectorBanks from '@/components/steps/fi-selector/FinancialSelectorBanks'
import FinancialSelectorPopup from '@/components/steps/fi-selector/FinancialSelectorPopup'
import FinancialSelectorFooter from '@/components/steps/fi-selector/FinancialSelectorFooter'
import DataLayer from '@/utilities/data-layer'

const allBanks = require('@/static/banking/data.json')

export default {
  // Name
  name: 'FinancialSelector',

  components: {
    AppSearchInput,
    StepHeader,
    FinancialSelectorBanks,
    FinancialSelectorPopup,
    FinancialSelectorFooter
  },

  mixins: [
    navigation
  ],

  data () {
    return {
      invisibleStickySearchBar: false,
      stickySearchBar: false,
      searchText: '',
      institutions: allBanks,
      searching: false,
      showPopup: false,
      banks: [
        {
          name: 'The Toronto Dominion Bank',
          image: require('@/assets/img/banks/TD.png'),
          bgColor: '#F7FFF6',
          bgColorHover: '#54B948',
          plaid: 'ins_42',
          flinks: 4
        },
        {
          name: 'RBC',
          image: require('@/assets/img/banks/RBC.png'),
          bgColor: '#ECF6FF',
          bgColorHover: '#0059B3',
          plaid: 'ins_39',
          flinks: 3
        },
        {
          name: 'Scotiabank',
          image: require('@/assets/img/banks/Scotiabank.png'),
          bgColor: '#FFE4E4',
          bgColorHover: '#D32229',
          plaid: 'ins_38',
          flinks: 6
        },
        {
          name: 'Bank of Montreal',
          image: require('@/assets/img/banks/BMO.png'),
          bgColor: '#ECF6FF',
          bgColorHover: '#0059B3',
          plaid: 'ins_41',
          flinks: 1
        },
        {
          name: 'CIBC',
          image: require('@/assets/img/banks/CIBC.png'),
          bgColor: '#FFE4E4',
          bgColorHover: '#8B1D41',
          plaid: 'ins_37',
          flinks: 5
        },
        {
          name: 'Tangerine',
          image: require('@/assets/img/banks/Tangerine.png'),
          bgColor: '#FFE0CC',
          bgColorHover: '#EA7023',
          plaid: 'ins_40',
          flinks: 8
        },
        {
          name: `ATB Financial`,
          image: require('@/assets/img/banks/ATB.png'),
          bgColor: '#ECF6FF',
          bgColorHover: '#1F61AD',
          plaid: 'ins_120010',
          flinks: 9
        },
        {
          name: `Simplii Financial`,
          image: require('@/assets/img/banks/SIMPLII.png'),
          bgColor: '#ECF6FF',
          bgColorHover: '#222945',
          plaid: 'ins_115631',
          flinks: 17
        },
        {
          name: `National Bank Of Canada`,
          image: require('@/assets/img/banks/National.png'),
          bgColor: '#FFE4E4',
          bgColorHover: '#D32229',
          flinksOverride: true,
          plaid: 'ins_48',
          flinks: 7
        },
        {
          name: `Desjardins`,
          image: require('@/assets/img/banks/Desjardins.png'),
          bgColor: '#F7FFF6',
          bgColorHover: '#008550',
          plaid: 'ins_46',
          flinks: 2
        },
        {
          name: `EQ Bank`,
          image: require('@/assets/img/banks/EQ.png'),
          bgColor: '#FFF6D7',
          bgColorHover: '#FFC800',
          plaid: 'ins_122886',
          flinks: 18
        },
        {
          name: `Peoples Trust`,
          image: require('@/assets/img/banks/Peoples.png'),
          bgColor: '#F7FFF6',
          bgColorHover: '#779B49',
          plaid: 'ins_133539',
          flinks: 36
        },
        {
          name: `Vancity`,
          image: require('@/assets/img/banks/Vancity.png'),
          bgColor: '#FFE4E4',
          bgColorHover: '#D32229',
          plaid: 'ins_115575',
          flinks: 15
        },
        {
          name: `Meridian Credit Union`,
          image: require('@/assets/img/banks/Meridian.png'),
          bgColor: '#ECF6FF',
          bgColorHover: '#005580',
          plaid: 'ins_118297',
          flinks: 11
        },
        {
          name: 'Coast Capital Savings',
          image: require('@/assets/img/banks/CC.png'),
          plaid: 'ins_119424',
          flinks: 13,
          bgColor: '#ECF6FF',
          bgColorHover: '#005580'
        },
        {
          name: `Servus Credit Union`,
          image: require('@/assets/img/banks/Servus.png'),
          bgColor: '#F7FFF6',
          bgColorHover: '#008550',
          plaid: 'ins_118849',
          flinks: 23,
          hideDesktop: true
        }
      ]
    }
  },

  computed: {
    filteredInstitutions () {
      if (!this.searchText) return []
      return this.institutions.filter(institution => {
        // hide flinks capital if demo is not enabled
        if (!process.env.VUE_APP_FLINKS_DEMO_ENABLED && institution.name === 'FlinksCapital') return false
        return institution.name.toLowerCase().includes(this.searchText.toLowerCase())
      }).splice(0, 5)
    },

    placeholder () {
      return this.searching ? 'e.g. TD Canada Trust' : 'Search for your bank'
    },

    isMobile () {
      return this.$store.state.tracking.device_type === 'm' && window.innerWidth <= 430
    },

    masterBankingProvider () {
      return this.$store.state.application.masterBankingProvider
    },

    bankingSplitter () {
      return this.$store.state.application.bankingSplitter
    },
    isFoundation () {
      return this.$store.getters['loans/isFoundation']
    }
  },

  mounted () {
    DataLayer.pushObject({ event: `spring_${this.eventPrefix}_fi-selector_page_landed` })
    if (this.eventPrefix === 'ploan') {
      window.rudderanalytics.track('spring_ploan_fi-selector_page_landed')
    }
  },

  methods: {
    handleInput (value) {
      this.searchText = value
    },

    handleSelect (bank) {
      this.searchText = ''
      this.handleBankSelect(bank, 'search-bar')
    },

    handleClose () {
      this.searching = false
      const eventName = `spring_${this.eventPrefix}_fi-selector_search_exit`
      DataLayer.pushObject({ event: eventName })
      this.searchText = ''
    },

    handleFocus () {
      this.stickySearchBar = false
      this.searching = true
      const eventName = `spring_${this.eventPrefix}_fi-selector_search_enter`
      DataLayer.pushObject({ event: eventName })
      if (this.isMobile) {
        this.$_scroll_toElement('body', window, 200)
      }
    },

    handleBankSelect (bank, source) {
      // set flinks and plaid institution ids based on bank selected
      this.$store.dispatch('setFlinksInstitutionId', bank.flinks)
      this.$store.dispatch('setPlaidInstitutionId', bank.plaid)

      const eventObj = {
        event: `spring_${this.eventPrefix}_fi-selector_bank_selected`,
        bank: bank.name,
        source: source
      }

      DataLayer.pushObject(eventObj)

      if (this.eventPrefix === 'ploan') {
        window.rudderanalytics.track('spring_ploan_fi-selector_bank_selected', {
          bank: bank.name,
          source: source
        })
      }

      this.$store.dispatch('setBank', bank.name)

      // FE Override fo Bank Provider
      const bankProvider = this.$route.query.bankingProvider || this.$route.query.banking_provider

      if (bankProvider && ((bankProvider === 'flinks') || (bankProvider === 'plaid'))) {
        this.$store.dispatch('setBankingProvider', bankProvider)
        return this.$_navigation_handleNext()
      }

      if (bank.name === 'FlinksCapital') {
        this.$store.dispatch('setBankingProvider', 'flinks')
        return this.$_navigation_handleNext()
      }

      // bank splitter logic
      const splitterBankProivider = this.bankingSplitter[bank.name]
      if (splitterBankProivider) {
        this.$store.dispatch('setBankingProvider', splitterBankProivider)
        return this.$_navigation_handleNext()
      }

      // if user is flinks set flinks only if flinks is available for bank
      if (this.masterBankingProvider === 'flinks' && bank.flinks) {
        this.$store.dispatch('setBankingProvider', 'flinks')
        return this.$_navigation_handleNext()
      }

      // set all banks to use plaid unless it is national bank and rbc, or bank doesn't support plaid
      if (!bank.plaid || (process.env.VUE_APP_FI_SELECTOR_FORCE_FLINKS_INSTITUTIONS && process.env.VUE_APP_FI_SELECTOR_FORCE_FLINKS_INSTITUTIONS.includes(bank.plaid))) {
        this.$store.dispatch('setBankingProvider', 'flinks')
      } else {
        this.$store.dispatch('setBankingProvider', 'plaid')
      }

      this.$_navigation_handleNext()
    },

    animationVisibilityChanged (isVisible) {
      if (!this.isMobile) return
      this.stickySearchBar = !isVisible
    },

    handleInvisibility (isVisible) {
      if (!this.isMobile) return
      if (!isVisible) {
        // show sticky search bar only if we are not looking at the footer
        if (window.scrollY < 1700) {
          this.invisibleStickySearchBar = isVisible
        }
      } else {
        this.invisibleStickySearchBar = isVisible
      }
    }

  }
}
</script>

<style lang="scss">

.financial-selector {
  @apply container;

  &__search {
    @apply w-full;

    &-list {
      @apply bg-white px-30 max-w-700 mt-5 mx-auto;
    }

    &--sticky {
      @screen xs-only {
        @apply fixed px-30;

        left: 0%;
        top: 75px;
      }
    }
  }
}
</style>
